import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip, useMessage } from 'components/ui';
import {
  AtlasGqlDispatchMode,
  useSubscribeToNotificationsForWorkContainerMutation,
  useUnsubscribeFromNotificationsForWorkContainerMutation,
  useWorkContainerNotificationSubscriptionSettingsQuery,
} from 'types/atlas-graphql';

import { StyledButton } from './NotificationSubscriptions.style';

type Props = {
  containerIds: string[];
  afterSubscribe?: () => void;
};

export const BulkNotificationSubscriptionButton: React.FC<Props> = ({
  containerIds,
  afterSubscribe,
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const [subscribed, setSubscribed] = useState<boolean>(false);

  const workContainerNotificationCopy = t(
    'workOrder.workContainerNotificationCopy',
    'You will receive Notifications for new Comments, change in Status, and new Attachments'
  );

  // TODO: to support in-app notifications, duplicate this query with AtlasGqlDispatchMode.InApp and treat with an "or"
  const { data: workContainerEmailSubscriptionSettingsData } =
    useWorkContainerNotificationSubscriptionSettingsQuery({
      variables: { input: { dispatchMode: AtlasGqlDispatchMode.Email } },
    });
  const notificationsEnabled =
    !!workContainerEmailSubscriptionSettingsData?.workContainerNotificationSubscriptionSettings
      .enabled;

  const [subscribe, { loading: subscribeLoading }] =
    useSubscribeToNotificationsForWorkContainerMutation({});
  const [unsubscribe, { loading: unsubscribeLoading }] =
    useUnsubscribeFromNotificationsForWorkContainerMutation({});

  const handleSubscribe = async () => {
    try {
      await Promise.all(
        containerIds.map(containerId => subscribe({ variables: { input: { containerId } } }))
      );

      setSubscribed(true);

      if (!!afterSubscribe) {
        afterSubscribe();
      }
    } catch (error) {
      console.error(error);
      message.error(
        t('workOrder.errorSubscribing', 'There was an issue subscribing. Please try again')
      );
    }
  };
  const handleUnsubscribe = async () => {
    try {
      await Promise.all(
        containerIds.map(containerId => unsubscribe({ variables: { input: { containerId } } }))
      );

      setSubscribed(false);
    } catch (error) {
      console.error(error);
      message.error(
        t('workOrder.errorUnsubscribing', 'There was an issue unsubscribing. Please try again')
      );
    }
  };

  if (subscribed) {
    return (
      <Tooltip
        title={
          notificationsEnabled ? (
            t(
              'workOrder.subscribedNotificationsTooltip',
              'You are subscribed to Notifications for {{taskPhrase}}. {{copy}}. Click to Unsubscribe.',
              {
                taskPhrase:
                  containerIds.length === 1
                    ? t('workOrder.thisTask', 'this Task')
                    : t('workOrder.eachOfTheseTasks', 'each of these Tasks'),
                copy: workContainerNotificationCopy,
              }
            )
          ) : (
            <>
              {t(
                'workOrder.subscribedNotificationsDisabledTooltip',
                "You're subscribed to Notifications for {{taskPhrase}} but have Notifications disabled. You can re-enable notifications in",
                {
                  taskPhrase:
                    containerIds.length === 1
                      ? t('workOrder.thisTask', 'this Task')
                      : t('workOrder.theseTasks', 'these Tasks'),
                }
              )}{' '}
              <a href="/settings/notification-settings" target="_blank">
                {t('workOrder.settings', 'settings')}
              </a>
              .
            </>
          )
        }
      >
        <span>
          <StyledButton
            onClick={handleUnsubscribe}
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={unsubscribeLoading}
            disabled={!notificationsEnabled}
          >
            {t('workOrder.subscribed', 'Subscribed')}
          </StyledButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        notificationsEnabled ? (
          t(
            'workOrder.clickToSubscribeTooltip',
            'Click to Subscribe to Notifications for {{taskPhrase}}. {{copy}}.',
            {
              taskPhrase:
                containerIds.length === 1
                  ? t('workOrder.thisTask', 'this Task')
                  : t('workOrder.eachOfTheseTasks', 'each of these Tasks'),
              copy: workContainerNotificationCopy,
            }
          )
        ) : (
          <>
            {t(
              'workOrder.enableWorkContainerNotifications',
              'To subscribe to Notifications, enable Work Container Notifications in'
            )}{' '}
            <a href="/settings/notification-settings" target="_blank">
              {t('workOrder.settings', 'settings')}
            </a>
            .
          </>
        )
      }
    >
      <span>
        <StyledButton
          data-testid="accept-bulk-subscription-button"
          style={{ width: '100%' }}
          onClick={handleSubscribe}
          icon={<BellOutlined />}
          loading={subscribeLoading}
          disabled={!notificationsEnabled}
        >
          {t('workOrder.subscribe', 'Subscribe')}
        </StyledButton>
      </span>
    </Tooltip>
  );
};
